import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/use-api";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { Suggestion } from "@/types/document";
import { ArrowRightToLine, Delete } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { resolveSentimentBorderColor } from "./blockUtils";

interface GroupSuggestionItemProps {
  suggestion: Suggestion;
  isSelected?: boolean;
  suggestionIndex: number;
  onProcess?: (processedIndex: number) => void;
}

export const GroupSuggestionItem = ({
  suggestion,
  isSelected = false,
  suggestionIndex,
  onProcess,
}: GroupSuggestionItemProps) => {
  const dispatch = useAppDispatch();
  const document = useAppState((state) => state.currentDocument);
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const currentRubric = useAppState((state) => state.currentRubric);
  const { approveSuggestion, rejectSuggestion } = useApi();

  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const suggestionRef = useRef<HTMLDivElement>(null);

  // Scroll into view when this suggestion is selected
  useEffect(() => {
    if (isSelected && suggestionRef.current) {
      requestAnimationFrame(() => {
        suggestionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  }, [isSelected]);

  // Tab to approve suggestion (only when this specific suggestion is selected)
  useHotkeys(
    "tab",
    async () => {
      if (isSelected && document) {
        await approveSuggestion(document.id, suggestion);
        onProcess?.(suggestionIndex);
      }
    },
    { keydown: true, enabled: isSelected },
    [
      isSelected,
      document,
      suggestion,
      suggestionIndex,
      approveSuggestion,
      onProcess,
    ]
  );

  // Backspace to reject suggestion (only when this specific suggestion is selected)
  useHotkeys(
    "backspace",
    async () => {
      if (isSelected && document && suggestion.id) {
        await rejectSuggestion(document.id, suggestion);
        onProcess?.(suggestionIndex);
      }
    },
    { keydown: true, enabled: isSelected },
    [
      isSelected,
      document,
      suggestion,
      suggestionIndex,
      rejectSuggestion,
      onProcess,
    ]
  );

  if (!document || !selectedGroup) return null;

  const borderColor = resolveSentimentBorderColor(
    suggestion.sentiment,
    suggestion.classification || undefined
  );

  const getReferenceInfo = (suggestion: Suggestion) =>
    suggestion.blocks.length === 0 ? "Entire document" : "Entire section";

  const getRubricCriteriaLabel = (suggestion: Suggestion) => {
    if (!suggestion.rubricCriteriaId || !currentRubric) return null;

    const criteria = currentRubric.criteria.find(
      (c) => c.id === suggestion.rubricCriteriaId
    );
    if (!criteria) return null;

    return criteria.criteria;
  };

  const handleApproveSuggestion = async (suggestion: Suggestion) => {
    setIsApproving(true);
    try {
      await approveSuggestion(document.id, suggestion);
      onProcess?.(suggestionIndex);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectSuggestion = async (suggestion: Suggestion) => {
    if (!suggestion.id) return;
    setIsDeleting(true);
    try {
      await rejectSuggestion(document.id, suggestion);
      onProcess?.(suggestionIndex);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuggestionClick = () => {
    if (typeof suggestionIndex === "number") {
      dispatch({
        type: "SET_SELECTED_SUGGESTION_INDEX",
        payload: suggestionIndex,
      });
    }
  };

  return (
    <div ref={suggestionRef} data-test-role="group-suggestion">
      <div className="flex">
        <div className="flex-1">
          <div
            className={cn(
              borderColor,
              "border-2 border-dashed flex-1 shadow-none rounded-lg py-2 cursor-pointer",
              isSelected && "bg-purple-50"
            )}
            onClick={handleSuggestionClick}
          >
            <div className="px-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm text-purple-600 my-0">
                    {suggestion.text}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-2 ml-2">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Refers to</span>
              <Badge variant="secondary" className="text-zinc-500 bg-zinc-50">
                {getReferenceInfo(suggestion)}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {getRubricCriteriaLabel(suggestion) && (
                <Badge variant="secondary" className="text-zinc-500 bg-zinc-50">
                  {getRubricCriteriaLabel(suggestion)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-col ml-4 w-6">
          {isSelected ? (
            <>
              <Button
                variant="outline"
                size="icon"
                aria-label="Approve suggestion"
                className="rounded-md border border-positive bg-positive text-positive hover:bg-green-100 hover:border-green-300 hover:text-green-700 p-2 w-8 h-8 flex items-center justify-center"
                onClick={() => handleApproveSuggestion(suggestion)}
                disabled={isApproving || isDeleting}
              >
                <ArrowRightToLine className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                aria-label="Reject suggestion"
                className="rounded-md border border-negative bg-negative text-negative hover:bg-red-100 hover:border-red-300 hover:text-red-700 p-2 w-8 h-8 flex items-center justify-center"
                onClick={() => handleRejectSuggestion(suggestion)}
                disabled={isApproving || isDeleting}
              >
                <Delete className="h-5 w-5" />
              </Button>
            </>
          ) : (
            <>
              <div className="h-8 w-8" />
              <div className="h-8 w-8" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
